import { EligibilityStatus, EncapsulatedFeatureKeys } from "@lyfegen-platform/graphql-schema/models";
import { EllipsisTypography, Typography } from "@lyfegen-platform/react-common";
import { groupBy, safeDivide, sortByField } from "@lyfegen-platform/utils";
import { Accordion, AccordionDetails, AccordionSummary, Box, Collapse, Divider, useTheme } from "@material-ui/core";
import { Palette } from "@material-ui/core/styles/createPalette";
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, InfoOutlined } from "@material-ui/icons";
import { userOrganization } from "apps/platform-client/client/src/store/layout";
import { defaultCompare } from "libs/utils/src/functions/default-compare";
import { useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import AdjustmentTooltip, { AdjustmentTooltipProps } from "../../../components/AdjustmentTooltip";
import { CaseDetailsRecordState, ConditionIconState } from "../../../components/CaseDetailsRecordState";
import DraggableWindowRoot, { DataForAdjustmentProps } from "../../../components/draggable-window/DragabbleWindowRoot";
import Adjust from "../../../components/icons/Adjust";
import { Matched } from "../../../components/icons/ConditionStatus/Matched";
import { NotRelevant } from "../../../components/icons/ConditionStatus/NotRelevant";
import { Pending } from "../../../components/icons/ConditionStatus/Pending";
import { Unmatched } from "../../../components/icons/ConditionStatus/Unmatched";
import LyfeSimpleTable, { SubRowType, TableColumnAlignmentType } from "../../../components/LyfeTable/LyfeSimpleTable";
import MappedComponent from "../../../components/mapped-components/MappedComponent";
import { getRecordStatusCaseDetailsComponent } from "../../../components/mapped-components/utils/status";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../models/common/data";
import { DocumentStatus, DocumentType, FlexFormStatus, RefundStatus, SmartMipPositionStatus } from "../../../models/generated/graphql";
import renderCurrency from "../../../utils/currency";
import formatCurrency from "../../../utils/currency-format";
import { localeDate } from "../../../utils/locale-date";
import startUpper from "../../../utils/start-upper";
import { GetCaseByIdQuery } from "../types/queries.generated";
import useStyles from "../use-styles";
import { PLACEHOLDER_XX } from "./Overview";

export enum ConditionArea {
	ENROLLMENT,
	ELIGIBILITY,
	CONCLUSIVE,
}

export enum SubFinancialType {
	DELTA,
	VARIABLE,
	INITIAL,
}

export type CaseFormStatus = "submitted" | "due" | "overdue" | undefined | null;

export type ConditionState = "matched" | "unmatched" | "pending" | "not-relevant";

export interface ICondition {
	state: ConditionState;
	name: string;
	value: string;
	conditionType: ConditionArea;
}

export type Record = [
	string,
	Array<
		GetCaseByIdQuery["cases"][number]["financialDataPoints"][number] & {
			conditions: Array<ICondition>;
			isRevocable: boolean;
			claimEligibilityStatus: EligibilityStatus;
		}
	>
];

export interface RecordsCardProps {
	title: string;
	caseId?: string;
	currency: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"] | undefined;
	records: Array<Record>;
	isDrawerOpen: boolean;
	canUserViewFinancials: boolean;
	refetch: any;
}

const ConditionIcon = (props: { state: ConditionState }) => {
	switch (props.state) {
		case "matched":
			return <Matched />;
		case "unmatched":
			return <Unmatched />;
		case "pending":
			return <Pending />;
		case "not-relevant":
			return <NotRelevant />;
	}
};

const getConditionState = (conditions: Array<ICondition>, conditionType: ConditionArea): ConditionIconState => {
	const relevantConditions = conditions.filter((condition) => condition.conditionType === conditionType);

	if (conditionType === ConditionArea.CONCLUSIVE) {
		if (relevantConditions.some((condition) => condition.state === "matched")) {
			return "positive";
		}

		if (relevantConditions.every((condition) => condition.state === "not-relevant")) {
			return "neutral";
		}

		if (
			relevantConditions.some((condition) => condition.state === "pending") &&
			relevantConditions.every((condition) => condition.state !== "matched")
		) {
			return "uncertain";
		}

		if (
			relevantConditions.some((condition) => condition.state === "unmatched") &&
			relevantConditions.every((condition) => condition.state !== "pending" && condition.state !== "matched")
		) {
			return "negative";
		}
	}

	if (conditionType === ConditionArea.ELIGIBILITY) {
		if (relevantConditions.length === 0) return "neutral";
		if (relevantConditions.every((condition) => condition.state === "matched")) return "positive";
		if (relevantConditions.some((condition) => condition.state === "unmatched")) return "negative";

		return "uncertain";
	}

	return "neutral";
};

type getSubRowsArgs = {
	financialDataPoints: Array<
		GetCaseByIdQuery["cases"][number]["financialDataPoints"][number] & {
			conditions: Array<ICondition>;
		}
	>;
	subRowType: SubFinancialType;
	currency: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"] | undefined;
	canUserViewFinancials: boolean;
	palette: Palette;
	canAdjustFromTierAndAccess: boolean;
	currentlyAdjustedRefundId: string | undefined;
	t: TFunction;
};

/* The subRowType check (check if variable amount exists) is done for AC18 of us 11070  */
const getSubRows = ({
	financialDataPoints,
	subRowType,
	currency,
	canUserViewFinancials,
	palette,
	canAdjustFromTierAndAccess,
	currentlyAdjustedRefundId,
	t,
}: getSubRowsArgs) => {
	return financialDataPoints.flatMap((financialDataPoint) => {
		const { sourceDocumentState, sourceRefund } = financialDataPoint;
		const canAdjust =
			canAdjustFromTierAndAccess &&
			sourceRefund?.status &&
			(sourceRefund.status === RefundStatus.New || sourceRefund.status === RefundStatus.Pending) &&
			(!sourceRefund.documentFinancialDataPointConnection.edges[0]?.smartMipStatus ||
				sourceRefund.documentFinancialDataPointConnection.edges[0]?.smartMipStatus === SmartMipPositionStatus.FurtherEnquiry ||
				sourceRefund.documentFinancialDataPointConnection.edges[0]?.smartMipStatus === SmartMipPositionStatus.Cancelled) &&
			getCanAdjust(
				!!sourceRefund?.isAgreementBuyer,
				!!sourceRefund?.isAgreementSeller,
				!!sourceDocumentState?.isConfirmedByBuyer,
				!!sourceDocumentState?.isConfirmedBySeller,
				sourceDocumentState?.type,
				sourceDocumentState?.status
			);
		const adjustmentTooltipProps = getAdjustmentTooltipProps(
			!!financialDataPoint.sourceRefund?.adjustedAmount,
			canUserViewFinancials,
			sourceRefund?.amount,
			currency?.Code || "CHF",
			sourceRefund
		);
		const adjustmentWindowData =
			sourceRefund &&
			getAdjustmentWindowData({
				isAgreementBuyer: !!sourceRefund?.isAgreementBuyer,
				isAgreementSeller: !!sourceRefund?.isAgreementSeller,
				refundable: sourceRefund?.amount,
				adjustedAmount: sourceRefund.adjustedAmount,
				quantity: financialDataPoint.quantity,
				basePrice: financialDataPoint.basePrice,
				invoiceId: sourceDocumentState?.id,
				refundId: sourceRefund.id,
				documentRefundGroupId: sourceRefund.documentRefundGroupId,
				currency: currency?.Code || "CHF",
				type: sourceRefund.creatableDocument,
				status: sourceDocumentState?.status,
			});
		const isAdjusted = !!financialDataPoint.sourceRefund?.adjustedAmount;

		if (subRowType === SubFinancialType.DELTA) {
			return [
				{
					subRowType: SubRowType.SAME_COLUMNS,
					payload: {
						administrationDate: EMPTY_VALUE_PLACEHOLDER,
						conditions: EMPTY_VALUE_PLACEHOLDER,
						pack: EMPTY_VALUE_PLACEHOLDER,
						quantity: EMPTY_VALUE_PLACEHOLDER,
						basePrice: EMPTY_VALUE_PLACEHOLDER,
						isAdjusted,
						isBeingAdjusted: sourceRefund?.id === currentlyAdjustedRefundId,
						status: getRecordStatusCaseDetailsComponent(
							financialDataPoint.evaluates.status!,
							sourceRefund?.status,
							null,
							0,
							palette,
							t
						),

						discountRate: getFormattedDiscountRate(
							sourceRefund?.adjustedRate ? sourceRefund?.adjustedRate : financialDataPoint.refundablePercentage,
							financialDataPoint,
							canUserViewFinancials,
							t
						),
						discountAmount: canUserViewFinancials
							? renderCurrency(
									sourceRefund?.adjustedAmount ? sourceRefund?.adjustedAmount : financialDataPoint.refundable || 0,
									currency?.Code,
									2,
									2
							  )
							: PLACEHOLDER_XX,
						canAdjust,
						adjustmentTooltipProps,
						adjustmentWindowData,
					},
				},
			];
		}

		const initialRow = {
			subRowType: SubRowType.SAME_COLUMNS,
			payload: {
				isAdjusted,
				isBeingAdjusted: financialDataPoint.sourceRefund?.id === currentlyAdjustedRefundId,
				administrationDate: EMPTY_VALUE_PLACEHOLDER,
				conditions: EMPTY_VALUE_PLACEHOLDER,
				pack: EMPTY_VALUE_PLACEHOLDER,
				quantity: EMPTY_VALUE_PLACEHOLDER,
				basePrice: EMPTY_VALUE_PLACEHOLDER,
				status: getRecordStatusCaseDetailsComponent(
					FlexFormStatus.Matched,
					financialDataPoint.sourceRefund?.status,
					null,
					0,
					palette,
					t
				),
				discountRate: getFormattedDiscountRate(
					financialDataPoint.sourceRefund?.adjustedRate
						? financialDataPoint.sourceRefund?.adjustedRate
						: financialDataPoint.sourceRefund?.refundRate || financialDataPoint.refundablePercentage,
					financialDataPoint,
					canUserViewFinancials,
					t
				),
				discountAmount: canUserViewFinancials
					? renderCurrency(
							financialDataPoint.sourceRefund?.adjustedAmount
								? financialDataPoint.sourceRefund?.adjustedAmount
								: financialDataPoint.sourceRefund?.amount || financialDataPoint.refundable,
							currency?.Code,
							2,
							2
					  )
					: PLACEHOLDER_XX,
				canAdjust,
				adjustmentTooltipProps,
				adjustmentWindowData,
			},
		};

		if (subRowType === SubFinancialType.INITIAL) {
			return [{ ...initialRow }];
		}

		// For Rows with variable
		return [
			{
				subRowType: SubRowType.SAME_COLUMNS,
				payload: {
					administrationDate: EMPTY_VALUE_PLACEHOLDER,
					conditions: EMPTY_VALUE_PLACEHOLDER,
					pack: EMPTY_VALUE_PLACEHOLDER,
					quantity: EMPTY_VALUE_PLACEHOLDER,
					basePrice: EMPTY_VALUE_PLACEHOLDER,
					status: getRecordStatusCaseDetailsComponent(FlexFormStatus.Matched, undefined, null, 0, palette, t),
					discountRate: getFormattedDiscountRate(
						financialDataPoint.variablePercentage,
						financialDataPoint,
						canUserViewFinancials,
						t
					),
					discountAmount: canUserViewFinancials
						? getVariableDiscountElement(financialDataPoint.variable, t, currency)
						: PLACEHOLDER_XX,
				},
			},
		];
	});
};

const getVariableDiscountElement = (
	amount: number,
	t: TFunction,
	currency: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"] | undefined
) => {
	const tooltipPropsVariableAmount = {
		title: t("variable-tooltip"),
		placement: "top-start",
		children: (
			<Box display="inline-block" borderBottom="1px dashed #969392">
				{renderCurrency(amount, currency?.Code, 2, 2)}
			</Box>
		),
	};

	return (
		<Box>
			<MappedComponent component="Tooltip" props={tooltipPropsVariableAmount} />
		</Box>
	);
};

const createConditionCards = (
	t: TFunction<"translation", undefined>,
	palette: Palette,
	conditions: Array<ICondition>,
	isDrawerOpen: boolean
) => {
	const groupedConditions = groupBy(conditions, (condition) => condition.conditionType);

	const renderConditionRow = (condition: ICondition) => (
		<>
			<Box display="flex" flexDirection="row" justifyContent="space-between" padding="16px" width="100%">
				<Box marginRight="4px" display="flex" flexDirection="row" width="50%">
					<Box marginRight="8px" paddingTop="4px" width="15%">
						<ConditionIcon state={condition.state} />
					</Box>
					<Box width="35%">
						<EllipsisTypography text={t(condition.name)} truncate />
					</Box>
				</Box>
				<Box width="50%">
					<Typography align="right">{condition.value}</Typography>
				</Box>
			</Box>
			<Divider />
		</>
	);

	const cardStyle = {
		backgroundColor: "#ffffff",
		width: isDrawerOpen ? "100%" : "30%",
		borderRadius: "4px ",
		marginBottom: isDrawerOpen ? "24px" : undefined,
	};

	const tooltipPropsEnrollingConditions = {
		title: t("enrolment-conditions-tooltip") || "",
		placement: "top-start",
		children: <InfoOutlined htmlColor={palette.primary.main} />,
	};
	const tooltipPropsEligibilityConditions = {
		title: t("eligibility-conditions-tooltip") || "",
		placement: "top-start",
		children: <InfoOutlined htmlColor={palette.primary.main} />,
	};
	const tooltipPropsConclusiveConditions = {
		title: t("conclusive-conditions-tooltip") || "",
		placement: "top-start",
		children: <InfoOutlined htmlColor={palette.primary.main} />,
	};

	return {
		subRowType: SubRowType.FULL_WIDTH,
		payload: (
			<Box paddingLeft="24px" paddingRight="24px" paddingTop="32px" paddingBottom="32px">
				<Typography style={{ paddingBottom: "24px" }}>{t("case-details-transaction-explanation")}</Typography>
				<Box display="flex" flexDirection={isDrawerOpen ? "column" : "row"} justifyContent="space-between">
					<Box style={cardStyle}>
						<Box display="flex" flexDirection="row" padding="16px">
							<Typography style={{ marginRight: "10px" }}>{t("enrolment-conditions").toUpperCase()}</Typography>
							<MappedComponent component="Tooltip" props={tooltipPropsEnrollingConditions} />
						</Box>
						<Divider />
						{groupedConditions
							.get(ConditionArea.ENROLLMENT)
							?.map((enrollmentCondition) => renderConditionRow(enrollmentCondition))}
					</Box>
					<Box style={cardStyle}>
						<Box display="flex" flexDirection="row" padding="16px">
							<Typography style={{ marginRight: "10px" }}>{t("eligibility-conditions").toUpperCase()}</Typography>
							<MappedComponent component="Tooltip" props={tooltipPropsEligibilityConditions} />
						</Box>

						<Divider />
						{groupedConditions
							.get(ConditionArea.ELIGIBILITY)
							?.map((eligibilityCondition) => renderConditionRow(eligibilityCondition))}
					</Box>
					<Box style={cardStyle}>
						<Box display="flex" flexDirection="row" padding="16px">
							<Typography style={{ marginRight: "10px" }}>{t("conclusive-conditions").toUpperCase()}</Typography>
							<MappedComponent component="Tooltip" props={tooltipPropsConclusiveConditions} />
						</Box>

						<Divider />
						{groupedConditions
							.get(ConditionArea.CONCLUSIVE)
							?.map((conclusiveCondition) => renderConditionRow(conclusiveCondition))}
					</Box>
				</Box>
			</Box>
		),
	};
};

const getFormattedDiscountRate = (
	refundablePercentage: number | null | undefined,
	initialDataPoint: any,
	canUserViewFinancials: boolean,
	t: TFunction
) => {
	if (!canUserViewFinancials) {
		return PLACEHOLDER_XX;
	}

	//Fixed Fee price
	if (refundablePercentage === null) {
		return `${formatCurrency(
			safeDivide(initialDataPoint.refundable, initialDataPoint.basePrice * initialDataPoint.quantity) * 100 || 0,
			2,
			2
		)}%`;
	}

	return `${formatCurrency(refundablePercentage || 0, 2, 2)}%`;
};

const getFormattedDiscountAmount = (
	initialDataPoint: GetCaseByIdQuery["cases"][number]["financialDataPoints"][number],
	deltaDataPoints: Array<GetCaseByIdQuery["cases"][number]["financialDataPoints"][number]>,
	canUserViewFinancials: boolean,
	currency: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"] | undefined,
	t: TFunction
) => {
	if (!canUserViewFinancials) {
		return PLACEHOLDER_XX;
	}

	const getAmount = () => {
		if (deltaDataPoints.length < 1) {
			return initialDataPoint.sourceRefund?.adjustedAmount
				? initialDataPoint.sourceRefund?.adjustedAmount
				: initialDataPoint.refundable || 0;
		} else {
			const deltasAmounts = deltaDataPoints.reduce((prev, curr) => {
				return curr.sourceRefund?.adjustedAmount
					? prev + curr.sourceRefund?.adjustedAmount
					: prev + (curr?.sourceRefund?.amount || 0);
			}, 0);
			return (
				(initialDataPoint.sourceRefund?.adjustedAmount
					? initialDataPoint.sourceRefund?.adjustedAmount
					: initialDataPoint?.sourceRefund?.amount || 0) + deltasAmounts
			);
		}
	};

	const amount = getAmount();

	return initialDataPoint.variable === 0
		? renderCurrency(amount, currency?.Code, 2, 2)
		: getVariableDiscountElement(amount + (initialDataPoint.variable || 0), t, currency);
};

const getCanAdjust = (
	isAgreementBuyer: boolean,
	isAgreementSeller: boolean,
	isConfirmedByBuyer: boolean,
	isConfirmedBySeller: boolean,
	type?: DocumentType | null,
	status?: DocumentStatus | null
) => {
	if (!type && !status) {
		return !isConfirmedByBuyer && !isConfirmedBySeller;
	}

	const isCreator = (isAgreementBuyer && type === DocumentType.Invoice) || (isAgreementSeller && type === DocumentType.CreditNote);
	const isConfirmedByReviewer = type === DocumentType.CreditNote ? isConfirmedByBuyer : isConfirmedBySeller;

	return !!(
		(!isCreator && status === DocumentStatus.InReview && !isConfirmedByReviewer) ||
		(isCreator && (status === DocumentStatus.InReview || status === DocumentStatus.WorkingDraft))
	);
};

type getAdjustmentWindowDataArgs = {
	isAgreementBuyer?: boolean;
	isAgreementSeller?: boolean;
	refundable?: number;
	adjustedAmount?: number | null;
	quantity?: number;
	basePrice?: number;
	invoiceId?: string;
	refundId?: string;
	documentRefundGroupId?: string | null;
	currency?: string;
	type?: DocumentType | null;
	status?: DocumentStatus | null;
	refetch?: any;
};

const getAdjustmentWindowData = ({ isAgreementBuyer, isAgreementSeller, refundId, type, status, refetch }: getAdjustmentWindowDataArgs) => {
	const isCreator = (type === DocumentType.Invoice && isAgreementBuyer) || (type === DocumentType.CreditNote && isAgreementSeller);

	return {
		refundId: refundId || "",
		isCreator,
		isInReview: status === DocumentStatus.InReview,
		refetch,
	} as DataForAdjustmentProps;
};

const getAdjustmentTooltipProps = (
	isAdjusted: boolean,
	canUserViewFinancials: boolean,
	refundable?: number,
	currency?: string,
	sourceRefund?: {
		adjustedAmount?: number | null;
		adjustedRate?: number | null;
		lastAdjustedAt?: string | null;
		adjustmentComment?: string | null;
		adjustedBy?: {
			firstName: string | null;
			lastName: string | null;
		} | null;
	} | null
) => {
	const adjustmentTooltipProps: AdjustmentTooltipProps = {
		wasAdjustedBefore: false,
		adjustedAmount: null,
		adjustedRate: null,
		originalAmount: null,
		currency: "",
		lastAdjustedAt: "",
		adjustedBy: "",
		adjustmentComment: "",
		canUserViewFinancials: canUserViewFinancials,
	};

	if (!sourceRefund || !isAdjusted) {
		return { ...adjustmentTooltipProps };
	}

	return {
		...adjustmentTooltipProps,
		wasAdjustedBefore: true,
		adjustedAmount: sourceRefund.adjustedAmount,
		adjustedRate: sourceRefund.adjustedRate,
		originalAmount: refundable,
		currency,
		lastAdjustedAt: localeDate(sourceRefund.lastAdjustedAt),
		adjustedBy: sourceRefund.adjustedBy ? `${sourceRefund.adjustedBy?.firstName} ${sourceRefund.adjustedBy?.lastName}` : "",
		adjustmentComment: sourceRefund.adjustmentComment || "",
	};
};

const RecordsCard = (props: RecordsCardProps) => {
	const [isExpanded, setIsExpanded] = useState<boolean>(true);
	const { palette } = useTheme();
	const { bodyItemHeader, collapseTitle } = useStyles();
	const { t } = useTranslation();
	const organization = useRecoilValue(userOrganization);
	const isAdjustingEnabledByTier = organization.encapsulatedFeatures.some(
		(feature) => feature.isEnabled && feature.ofType.key === EncapsulatedFeatureKeys.DocumentAdjustment
	);
	const [isAdjustmentWindowOpen, setIsAdjustmentWindowOpen] = useState<boolean>(false);
	const [currentlyAdjustedRefunds, setCurrentlyAdjustedRefunds] = useState<Array<DataForAdjustmentProps>>([]);

	// TODO: review revocation from here
	// const [revokeRecordId, setRevokeRecordId] = useState<string | null>(null);
	// const [revokeRecord, { loading }] = useDataMutation(REVOKE_RECORD, {
	// 	refetchQueries: [{ query: GET_CASE_BY_ID, variables: { id: props.caseId } }],
	// });

	function toggleExpanded() {
		setIsExpanded(!isExpanded);
	}

	const handleOpenAdjustmentWindow = (refundData: any) => {
		if (isAdjustmentWindowOpen) {
			setIsAdjustmentWindowOpen(false);
			setCurrentlyAdjustedRefunds([]);
		}

		if (!refundData) {
			return;
		}

		setCurrentlyAdjustedRefunds([{ ...refundData }]);
		setIsAdjustmentWindowOpen(true);
	};

	const columns = [
		{
			headerName: "administration-date",
			key: "administrationDate",
			alignment: TableColumnAlignmentType.LEFT,
			tdStyle: { width: "10%" },
		},
		{
			headerName: "conditions",
			key: "conditions",
			alignment: TableColumnAlignmentType.CENTER,
			tdStyle: { width: "5%" },
		},
		{ headerName: "pack", key: "pack", alignment: TableColumnAlignmentType.LEFT, style: { width: "20%" } },
		{ headerName: "quantity", key: "quantity", alignment: TableColumnAlignmentType.RIGHT },
		{ headerName: "base-price", key: "basePrice", alignment: TableColumnAlignmentType.RIGHT },
		{ headerName: "status", key: "status", alignment: TableColumnAlignmentType.LEFT },
		{ headerName: "discount-rate", key: "discountRate", alignment: TableColumnAlignmentType.RIGHT },
		{ headerName: "discount-amount", key: "discountAmount", alignment: TableColumnAlignmentType.RIGHT },
	];

	const mappedRows = props.records
		.map(([_, records]) => {
			const initialDataPoint: GetCaseByIdQuery["cases"][number]["financialDataPoints"][number] & {
				conditions: Array<ICondition>;
				isRevocable: boolean;
				claimEligibilityStatus: EligibilityStatus;
			} = records.find((record) => !record.isDelta)!;
			const deltaDataPoints = records.filter((dataPoint) => dataPoint.isDelta && dataPoint.sourceRefund);
			const canAdjustFromTierAndAccess = isAdjustingEnabledByTier && props.canUserViewFinancials;
			const { sourceDocumentState, sourceRefund } = initialDataPoint;
			const canAdjust =
				canAdjustFromTierAndAccess &&
				!!sourceRefund?.status &&
				(sourceRefund.status === RefundStatus.New || sourceRefund.status === RefundStatus.Pending) &&
				(!sourceRefund.documentFinancialDataPointConnection.edges[0]?.smartMipStatus ||
					sourceRefund.documentFinancialDataPointConnection.edges[0]?.smartMipStatus === SmartMipPositionStatus.FurtherEnquiry ||
					sourceRefund.documentFinancialDataPointConnection.edges[0]?.smartMipStatus === SmartMipPositionStatus.Cancelled) &&
				getCanAdjust(
					!!sourceDocumentState?.isAgreementBuyer,
					!!sourceDocumentState?.isAgreementSeller,
					!!sourceDocumentState?.isConfirmedByBuyer,
					!!sourceDocumentState?.isConfirmedBySeller,
					sourceDocumentState?.type,
					sourceDocumentState?.status
				);
			const isAdjusted =
				props.canUserViewFinancials &&
				((sourceRefund && !!sourceRefund?.adjustedAmount) || deltaDataPoints.some((ddp) => ddp.sourceRefund?.adjustedAmount));

			const adjustmentTooltipProps = getAdjustmentTooltipProps(
				!!isAdjusted,
				props.canUserViewFinancials,
				initialDataPoint.refundable,
				props.currency?.Code || "CHF",
				sourceRefund
			);

			let adjustmentWindowData = null;

			if (canAdjust && sourceRefund && props.canUserViewFinancials) {
				adjustmentWindowData = getAdjustmentWindowData({
					isAgreementBuyer: !!sourceRefund.isAgreementBuyer,
					isAgreementSeller: !!sourceRefund.isAgreementSeller,
					refundable: initialDataPoint.refundable,
					adjustedAmount: sourceRefund.adjustedAmount,
					quantity: initialDataPoint.quantity,
					basePrice: initialDataPoint.basePrice,
					invoiceId: sourceDocumentState?.id,
					refundId: sourceRefund.id,
					documentRefundGroupId: sourceRefund.documentRefundGroupId,
					currency: props.currency?.Code || "CHF",
					type: sourceRefund.creatableDocument,
					status: sourceDocumentState?.status,
					refetch: props.refetch,
				});
			}

			const finalRate = () => {
				if (!initialDataPoint.sourceRefund) {
					return initialDataPoint.refundablePercentage;
				}

				const initialRate = initialDataPoint.sourceRefund.adjustedRate
					? initialDataPoint.sourceRefund.adjustedRate
					: initialDataPoint.sourceRefund.refundRate;

				if (deltaDataPoints.length < 1) {
					return initialRate;
				}

				const sumOfDeltasRates = deltaDataPoints.reduce((prev, curr) => {
					return curr.sourceRefund?.adjustedRate
						? prev + curr.sourceRefund?.adjustedRate
						: prev + (curr.sourceRefund?.refundRate || 0);
				}, 0);

				return initialRate + sumOfDeltasRates;
			};

			return {
				recordId: initialDataPoint.uuid,
				administrationDate: localeDate(initialDataPoint.prescriptionDate),
				// used for sorting
				sourceAdministrationDate: initialDataPoint.prescriptionDate,
				conditions: (
					<CaseDetailsRecordState
						conclusiveConditionState={getConditionState(initialDataPoint.conditions, ConditionArea.CONCLUSIVE)}
						eligibilityConditionState={getConditionState(initialDataPoint.conditions, ConditionArea.ELIGIBILITY)}
					/>
				),
				pack: `${initialDataPoint.treatment?.regulatedBy?.brand?.Name} ${records[0].treatment?.Name}`,
				quantity: initialDataPoint.quantity,
				basePrice: props.canUserViewFinancials
					? renderCurrency(initialDataPoint.basePrice || 0, props.currency?.Code, 2, 2)
					: PLACEHOLDER_XX,
				status: getRecordStatusCaseDetailsComponent(
					initialDataPoint.evaluates.status!,
					initialDataPoint.sourceRefund?.status,
					initialDataPoint.claimEligibilityStatus,
					deltaDataPoints.length,
					palette,
					t
				),
				discountRate: getFormattedDiscountRate(finalRate(), initialDataPoint, props.canUserViewFinancials, t),
				discountAmount: getFormattedDiscountAmount(
					initialDataPoint,
					deltaDataPoints,
					props.canUserViewFinancials,
					props.currency,
					t
				),
				isAdjusted,
				isBeingAdjusted: initialDataPoint.sourceRefund?.id === currentlyAdjustedRefunds?.[0]?.refundId,

				subRows: [
					...(initialDataPoint.variable !== 0
						? getSubRows({
								financialDataPoints: [initialDataPoint],
								subRowType: SubFinancialType.VARIABLE,
								currency: props.currency,
								canUserViewFinancials: props.canUserViewFinancials,
								palette: palette,
								canAdjustFromTierAndAccess: canAdjustFromTierAndAccess,
								currentlyAdjustedRefundId: currentlyAdjustedRefunds?.[0]?.refundId,
								t,
						  })
						: []),
					...getSubRows({
						financialDataPoints: deltaDataPoints,
						subRowType: SubFinancialType.DELTA,
						currency: props.currency,
						canUserViewFinancials: props.canUserViewFinancials,
						palette: palette,
						canAdjustFromTierAndAccess: canAdjustFromTierAndAccess,
						currentlyAdjustedRefundId: currentlyAdjustedRefunds?.[0]?.refundId,
						t,
					}),
					...(initialDataPoint.variable === 0 && deltaDataPoints.length > 0
						? getSubRows({
								financialDataPoints: [initialDataPoint],
								subRowType: SubFinancialType.INITIAL,
								currency: props.currency,
								canUserViewFinancials: props.canUserViewFinancials,
								palette: palette,
								canAdjustFromTierAndAccess: canAdjustFromTierAndAccess,
								currentlyAdjustedRefundId: currentlyAdjustedRefunds?.[0]?.refundId,
								t,
						  })
						: []),
					createConditionCards(t, palette, initialDataPoint.conditions, props.isDrawerOpen),
				],
				cellStyle: { opacity: initialDataPoint.revokedOn ? 0.4 : 1 },
				// TODO: review revocation from here
				isActionButtonDisabled: !initialDataPoint.isRevocable || !!initialDataPoint.revokedOn /*|| loading*/,
				isActionButtonLoading: false /*initialDataPoint.uuid === revokeRecordId && loading*/,
				actionButtonTooltipText: t("revoke"),
				adjustmentWindowData,
				isSecondActionButtonDisabled: !canAdjust /*|| loading*/,
				isSecondActionButtonLoading: false /*initialDataPoint.uuid === revokeRecordId && loading,*/,
				secondActionButtonTooltipText: isAdjusted ? (
					<AdjustmentTooltip {...adjustmentTooltipProps} />
				) : (
					t("document-transaction-adjust")
				),
			};
		})

		.sort(
			sortByField("sourceAdministrationDate", (administrationDate1, administrationDate2) =>
				defaultCompare(administrationDate2, administrationDate1)
			)
		);

	// function handleRevokeRecord(index: number) {
	// 	const recordId = mappedRows[index].recordId;
	// 	setRevokeRecordId(recordId);
	// 	revokeRecord({ variables: { id: recordId } });
	// }

	return (
		<>
			<Accordion
				defaultExpanded
				onChange={toggleExpanded}
				expanded={true}
				TransitionProps={{ unmountOnExit: true }}
				style={{ borderRadius: "0px 0px 12px 12px" }}
				elevation={0}
			>
				<AccordionSummary
					className={bodyItemHeader}
					style={{ display: "flex", flexDirection: "row-reverse" }}
					expandIcon={isExpanded ? <ExpandMoreIcon color={"inherit"} /> : <ExpandLessIcon color={"inherit"} />}
					aria-controls={props.title}
					id={props.title}
				>
					<Typography className={collapseTitle} type="h5" color={"textSecondary"}>
						{startUpper(t(props.title))}
					</Typography>
				</AccordionSummary>
				<Collapse in={isExpanded}>
					<Box justifyContent="space-between" width="100%" display="flex" alignItems="center" pb={2}>
						<Divider style={{ flexGrow: 1, backgroundColor: palette.tableDivider.main }} />
					</Box>
					<AccordionDetails>
						<LyfeSimpleTable
							fixed
							noDataLocalizationKey="case-details-no-records"
							alternatingBackgroundColor="rgba(126,166,180,0.08)"
							columns={columns}
							rows={mappedRows}
							divider={true}
							areSubRowsEnabled={true}
							// actionButtonIcon={<ClearIcon />}
							// actionButtonCallback={(index: number) => handleRevokeRecord(index)}
							secondActionButtonIcon={props.canUserViewFinancials && <Adjust fill="#367bf5" />}
							secondActionButtonDisabledIcon={<Adjust fill="#969392" />}
							secondActionButtonCallback={handleOpenAdjustmentWindow}
							rowIdKey="record"
						/>
					</AccordionDetails>
				</Collapse>
			</Accordion>
			{isAdjustingEnabledByTier && isAdjustmentWindowOpen && (
				<DraggableWindowRoot
					open={isAdjustmentWindowOpen}
					close={() => setIsAdjustmentWindowOpen(false)}
					currentlyAdjustedRefunds={currentlyAdjustedRefunds}
				/>
			)}
		</>
	);
};

export default RecordsCard;
